<template>
  <div class="home-page">
    <div class="banner">
      <ClientOnly>
        <template #fallback>
          <img
            v-if="!isMobile"
            :src="BannerWebFirstFramePNG"
            alt=""
            class="banner-web-first-frame"
          >
        </template>
      </ClientOnly>
      <div class="banner-main">
        <h1 class="public-page-title">
          {{ $t('home-banner-title') }}
        </h1>
        <h2 class="public-page-subtitle">
          <span
            v-for="el in subTitleConfig"
            :key="el.class"
            :class="el.class"
          >
            <span class="icon" />
            {{ el.title }}
          </span>
        </h2>
        <div
          v-show="!isMobile"
          class="public-page-input"
        >
          <ElInput
            ref="inputRef"
            v-model="inputValue"
            v-saw="() => trackAreaVisible('try')"
            :placeholder="$t('home-banner-input-placeholder')"
            @blur="handleInputBlur"
            @click="
              handleInputClick();
              trackMainPageClick('input_click');
            "
            @input="handleInput"
            @keydown.enter="
              handleParse();
              trackMainPageClick('try_for_free');
            "
          />
          <ElButton
            @click="
              handleParse();
              trackMainPageClick('try_for_free');
            "
          >
            {{ $t('home-banner-button-content') }}
          </ElButton>

          <ClientOnly>
            <ElPopover
              ref="inputPopoverRef"
              :virtual-ref="inputRef"
              :visible="inputPopoverVisible"
              popper-class="pp-input-popover --el-popover input-popover"
              virtual-triggering
            >
              <ul>
                <li
                  v-for="item of platformNav"
                  :key="item.id"
                  @click="
                    handleLinkClick(item.linkUrl);
                    handleParse()
                    trackInputDemoClick(item.type);
                  "
                >
                  <div class="media-type">
                    <img
                      :src="item.iconName"
                      alt=""
                    >
                    <label for=""> {{ item.type }}</label>
                  </div>
                  <a
                    class="media-link"
                    href="javascript:void(0);"
                  >
                    <div class="media-link-url">
                      {{ item.linkUrl }}
                    </div>
                    <div class="media-link-text">{{ item.description }}</div>
                  </a>
                </li>
              </ul>
              <p>{{ $t('home-banner-popover-content') }}</p>
            </ElPopover>
          </ClientOnly>
          <div
            v-if="!isMobile"
            class="resolve-source-error"
          >
            {{ resolveResourceState.errorMessage }}
          </div>
        </div>
        <AppInstallEntries v-if="isMobile" />
      </div>
      <ResolveSource
        ref="resolveResourceInstance"
        v-model:visible="resolveResourceState.visible"
        :url="resolveSourceUrl"
        @closed="handleResolveSourceClose"
      />
      <div
        v-show="!resolveResourceState.visible"
        ref="bannerAnimationContainer"
        :class="{ android: isAndroid }"
        class="banner-animation-container"
      />
    </div>

    <div class="highlight-explain">
      <h1 class="highlight-explain-title">
        {{ $t('home-explain-title') }}
      </h1>

      <div
        id="speed-card"
        v-saw="() => trackAreaVisible('speed')"
        class="speed-explain"
      >
        <div class="content speed-content">
          <div class="title">
            <h2>
              <span class="front">
                {{ $t('home-speed-title-front') }}</span>
              <span class="end">{{ $t('home-speed-title-end') }}</span>
            </h2>
          </div>

          <p class="subtitle">
            {{ $t('home-speed-subtitle') }}
          </p>

          <div
            class="start-pikpak"
            @click="
              handleStartPikpak();
              trackMainPageClick('speed_start');
            "
          >
            <span>{{ $t('home-speed-start') }}</span>
            <img
              :alt="$t('home-speed-start')"
              :src="rightArrowSVG"
              height="24"
              width="24"
            >
          </div>
        </div>

        <div class="speed-chart">
          <SpeedChart
            :count="10"
            :delay="0.3"
            :dur="10"
            :speed-data="speedData"
            @update="handleGetSpeedData"
          />
        </div>
      </div>

      <div
        id="safe-card"
        v-saw="() => trackAreaVisible('security')"
        class="safe-explain"
      >
        <div class="content safe-content">
          <div class="title">
            <h2>
              <span class="front">{{ $t('home-safe-title-front') }}</span>
              <span class="end">{{ $t('home-safe-title-end') }}</span>
            </h2>
          </div>

          <p class="subtitle">
            {{ $t('home-safe-subtitle') }}
          </p>
          <div
            class="start-pikpak"
            @click="
              handleStartPikpak();
              trackMainPageClick('security_start');
            "
          >
            <span>{{ $t('home-speed-start') }}</span>
            <img
              :alt="$t('home-speed-start')"
              :src="rightArrowSVG"
              height="24"
              width="24"
            >
          </div>
        </div>
        <div
          v-if="!isMobile"
          ref="safeAnimationContainer"
          class="safe-animation-container"
        >
          <div class="content-box url">
            <h3 class="name">
              {{ $t('home-safe-url-title') }}
            </h3>
            <p class="detail">
              {{ $t('home-safe-url-content') }}
            </p>
          </div>
          <div class="content-box private-cloud">
            <h3 class="name">
              {{ $t('home-safe-cloud-title') }}
            </h3>
            <p class="detail">
              {{ $t('home-safe-cloud-content') }}
            </p>
          </div>
          <div class="content-box device">
            <h3 class="name">
              {{ $t('home-safe-device-title') }}
            </h3>
            <p class="detail">
              {{ $t('home-safe-device-content') }}
            </p>
          </div>
        </div>
        <div v-else>
          <div class="safe-h5-content">
            <ElCarousel
              ref="safeH5CarouselRef"
              :autoplay="false"
              arrow="always"
              class="safe-carousel"
            >
              <!-- TODO 待优化 -->
              <div
                @touchmove="e => handleCarouselTouchmove(e, 'safe')"
                @touchstart="handleCarouselTouchstart"
              >
                <ElCarouselItem
                  v-for="item of safeH5ContentData"
                  :key="item.title"
                >
                  <img
                    :alt="item.title"
                    :src="item.imgUrl"
                  >
                  <div class="content-box">
                    <h3 class="title">
                      {{ item.title }}
                    </h3>
                    <p class="describe">
                      {{ item.describe }}
                    </p>
                  </div>
                </ElCarouselItem>
              </div>
            </ElCarousel>
            <div
              ref="safeAnimationH5Container"
              class="safe-animation-h5-container"
            />
          </div>
        </div>

        <span class="summary safe-summary">{{ $t('home-safe-summary') }}</span>
      </div>

      <div
        id="video-card"
        v-saw="() => trackAreaVisible('hd')"
        class="video-explain"
      >
        <div class="content video-content">
          <div class="title">
            <h2>
              <span class="front">{{ $t('home-video-title-front') }}</span>
              <span class="end">{{ $t('home-video-title-end') }}</span>
            </h2>
          </div>
          <p class="subtitle">
            {{ $t('home-video-subtitle') }}
          </p>

          <div
            class="start-pikpak"
            @click="
              handleStartPikpak();
              trackMainPageClick('hd_start');
            "
          >
            <span>{{ $t('home-speed-start') }}</span>
            <img
              :alt="$t('home-speed-start')"
              :src="rightArrowSVG"
              height="24"
              width="24"
            >
          </div>
        </div>

        <div
          v-saw="()=>handleVideoPlay()"
          class="video-diff-container"
        >
          <video
            ref="videoRef"
            loop
            muted
            playsInline
            poster="@/assets/images/home/video-placeholder.jpg"
          >
            <source type="video/mp4">
          </video>
        </div>

        <div class="video-diff-content">
          <div class="content-box">
            <span class="image-quality">{{ $t('home-video-quality-bad') }}</span>
            <span class="website-explain">{{ $t('home-video-website-play') }}</span>
          </div>

          <div class="content-box">
            <span class="image-quality">{{ $t('home-video-quality-good') }}</span>
            <span class="website-explain">{{ $t('home-video-pikpak-play') }}</span>
          </div>
        </div>

        <span class="summary video-summary">{{ $t('home-video-summary') }} </span>
      </div>
    </div>
    <div
      id="platform-card"
      v-saw="() => trackAreaVisible('platform')"
      class="platform-display"
    >
      <h1 class="platform-title">
        {{ $t('home-platform-title') }}
      </h1>
      <div class="platform-card-container">
        <ul v-if="!isMobile">
          <PlatformCard
            v-for="item of platformCardData"
            :key="item.cardTitle"
            :card-svg="item.cardSvg"
            :card-text="item.cardText"
            :card-title="item.cardTitle"
            :hover-svg="item.hoverSvg"
            :hover-text="item.hoverText"
            :hover-title="item.hoverTitle"
            :hover-hyperlinks="item.hoverHyperlinks"
            :link-url="item.linkUrl"
            :on-click="item.onClick"
            :top-right-angle="item.topRightAngle"
            :top-right-label="item.topRightLabel"
            :track-name="item.trackName"
          />
        </ul>
        <ul v-else>
          <li>
            <div class="platform-h5-box">
              <img
                :alt="$t('home-platform-telegram-h5-title')"
                :src="PlatformTelegramH5SVG"
                class="platform-h5-img"
                height="54"
                width="54"
              >
              <div class="platform-h5-label">
                <span>{{ $t('home-platform-telegram-h5-label') }}</span>
              </div>
              <h3 class="platform-h5-title">
                {{ $t('home-platform-telegram-h5-title') }}
              </h3>
              <p class="platform-h5-subtitle">
                {{ $t('home-platform-telegram-h5-subtitle') }}
              </p>
              <a
                :href="RoutePathOfHelp.TELEGRAM_BOT"
                rel="noopener noreferrer"
                target="_blank"
              >
                <ElButton class="platform-h5-button">
                  {{ $t('home-platform-telegram-h5-button') }}
                </ElButton>
              </a>
            </div>
          </li>
          <li>
            <div class="platform-h5-box no-label">
              <img
                :alt="$t('home-platform-windows-h5-title')"
                :src="PlatformPCH5SVG"
                class="platform-h5-img"
                height="54"
                width="54"
              >
              <h3 class="platform-h5-title">
                {{ $t('home-platform-windows-h5-title') }}
              </h3>
              <p class="platform-h5-subtitle">
                {{ $t('home-platform-windows-h5-subtitle') }}
              </p>
              <a
                class="platform-h5-link"
                disable
                href="javascript:void(0)"
              >* {{ $t('to-desktop-download') }}</a>
            </div>
          </li>
          <li>
            <div class="platform-h5-box no-label">
              <img
                alt="Mac OS"
                :src="PlatformMACH5SVG"
                class="platform-h5-img"
                height="54"
                width="54"
              >
              <h3 class="platform-h5-title">
                Mac OS
              </h3>
              <p class="platform-h5-subtitle">
                {{ $t('home-platform-card-data-mac-text') }}
              </p>
              <a
                class="platform-h5-link"
                disable
                href="javascript:void(0)"
              >* {{ $t('to-desktop-download') }}</a>
            </div>
          </li>
          <li>
            <div class="platform-h5-box no-label">
              <img
                alt="TV APP"
                :src="PlatformTVH5SVG"
                class="platform-h5-img"
                height="54"
                width="54"
              >
              <h3 class="platform-h5-title">
                TV APP
              </h3>
              <p class="platform-h5-subtitle">
                {{ $t('home-platform-card-data-android-text') }}
              </p>
              <a
                :href="RoutePath.TV_APP"
                rel="noopener noreferrer"
                target="_blank"
              >
                <ElButton class="platform-h5-button">
                  {{ $t('home-platform-tv-h5-button') }}
                </ElButton>
              </a>
            </div>
          </li>
          <li>
            <div class="platform-h5-box no-label">
              <img
                alt="Web DAV"
                :src="WebDAVSVG"
                class="platform-h5-img"
                height="54"
                width="54"
              >
              <h3 class="platform-h5-title">
                Web DAV
              </h3>
              <p class="platform-h5-subtitle">
                {{ $t('home-platform-card-web-dav-text') }}
              </p>
              <a
                :href="`${RoutePath.FAQ}#21`"
                rel="noopener noreferrer"
                target="_blank"
              >
                <ElButton class="platform-h5-button">
                  {{ $t('home-platform-telegram-h5-button') }}
                </ElButton>
              </a>
            </div>
          </li>
          <li>
            <div class="platform-h5-box no-label">
              <img
                :alt="$t('home-platform-web-h5-title')"
                :src="PlatformWebH5SVG"
                class="platform-h5-img"
                height="54"
                width="54"
              >
              <h3 class="platform-h5-title">
                {{ $t('home-platform-web-h5-title') }}
              </h3>
              <a
                :href="RoutePathOfDrive.ALL"
                rel="noopener noreferrer"
                target="_blank"
              >
                <ElButton class="platform-h5-button">
                  {{ $t('home-platform-web-h5-button') }}
                </ElButton>
              </a>
            </div>
          </li>
        </ul>
      </div>
    </div>
    <div
      id="payment-card"
      v-saw="() => trackAreaVisible('price')"
      class="price-comparison"
    >
      <h1 class="price-title">
        {{ $t('home-price-title') }}
      </h1>
      <ul
        v-if="!isMobile"
        class="price-card-container"
      >
        <PriceCard
          v-for="item of priceCardData"
          :key="item.headerName"
          :discount="saveDiscount"
          :header-name="item.headerName"
          :header-svg="item.headerSvg"
          :is-pikpak="item.isPikpak"
          :link-url="item.linkUrl"
          :price-unit="productPrice.priceUnit"
          :price-value="item.priceValue"
          :track-name="item.trackName"
        />
      </ul>

      <div
        v-else
        class="price-card-h5-container"
      >
        <ElCarousel
          ref="priceCarouselRef"
          :autoplay="false"
          :initial-index="1"
          arrow="never"
          class="price-carousel"
          type="card"
        >
          <!-- TODO 待优化 -->
          <div
            @touchmove="(e) => handleCarouselTouchmove(e, 'price')"
            @touchstart="handleCarouselTouchstart"
          >
            <ElCarouselItem
              v-for="item of priceCardData"
              :key="item.headerName"
            >
              <PriceCard
                :discount="saveDiscount"
                :header-name="item.headerName"
                :header-svg="item.headerSvg"
                :is-pikpak="item.isPikpak"
                :link-url="item.linkUrl"
                :price-unit="productPrice.priceUnit"
                :price-value="item.priceValue"
                :track-name="item.trackName"
              />
            </ElCarouselItem>
          </div>
        </ElCarousel>
      </div>
      <div class="price-sale-promotion">
        <div
          class="limit-tips"
          v-html="$t('pikpak-limit-tips')"
        />
        <div v-if="productPrice.hasDiscount">
          <div class="price-countdown">
            <div class="content">
              <img
                :alt="$t('home-price-sale-promotion')"
                :src="CountdownSVG"
                height="25"
                width="25"
              >
              <span class="text">{{ $t('home-price-sale-promotion') }}</span>
            </div>
            <Countdown />
          </div>
          <p class="today-price">
            {{ $t('home-price-today-1') }}
            <span class="value">
              {{ $t('currency-type') }}&nbsp;{{ productPrice.pikpakSalePrice }}
            </span>
            <span>&nbsp;/TB/{{ $t('month') }}&nbsp;</span>{{ $t('home-price-today-2') }}
          </p>
        </div>
        <a
          :href="RoutePathOfDrive.PAYMENT"
          rel="noopener noreferrer"
          target="_blank"
        >
          <ElButton
            class="get-discount"
            @click="trackMainPageClick('get_sale')"
          >
            {{ $t('home-price-getPromotion') }}
          </ElButton>
        </a>

        <p class="tips">
          {{ $t('home-price-tips') }}
        </p>
      </div>
    </div>

    <div
      id="comment-card"
      v-saw="() => trackAreaVisible('rating')"
      class="feedbacks"
    >
      <div class="public-page-content">
        <h2>{{ $t('home-feedbacks-title') }}</h2>
        <p class="home-feedbacks-subtitle">
          {{ $t('feedback-from-google-play') }}
        </p>
        <div
          ref="carouselWrap"
          class="carousel-wrap"
        >
          <ul
            :style="`transform: translateX(${transitionX})`"
            class="carousel"
          >
            <li
              v-for="(name, idx) in feedbackNumbers"
              :key="name"
            >
              <div class="info">
                <img
                  :alt="name"
                  :src="avatars[`${idx % avatars.length}`]"
                  class="avatar"
                  height="48"
                  width="48"
                >
                <div class="name">
                  {{ name }}
                </div>
              </div>
              <div class="feedback">
                {{ $t(`home-feedback-${idx + 1}`) }}
              </div>
            </li>
          </ul>
        </div>
        <div class="carousel-controller">
          <a
            :class="swipeBtnAttr(SwipeDirection.LEFT).class"
            href="javascript:void(0);"
            @click="swipe(SwipeDirection.LEFT)"
          >
            <DownIcon
              :color="swipeBtnAttr(SwipeDirection.LEFT).color"
              style="transform: rotate(90deg)"
            />
          </a>
          <a
            :class="swipeBtnAttr(SwipeDirection.RIGHT).class"
            href="javascript:void(0);"
            @click="swipe(SwipeDirection.RIGHT)"
          >
            <DownIcon
              :color="swipeBtnAttr(SwipeDirection.RIGHT).color"
              style="transform: rotate(-90deg)"
            />
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script
  lang="ts"
  setup
>
import {
  computed,
  type ComputedRef,
  defineAsyncComponent,
  definePageMeta,
  inject,
  nextTick,
  onMounted,
  reactive,
  ref,
  toRefs,
  useI18n,
  useRequestHeaders,
  useSeoMeta,
  watch,
  watchEffect,
  useRequestEvent,
} from '#imports'
import AndroidSVG from '@/assets/images/home/android.svg'
import ogImage from '@/assets/images/home/banner-main.png'
import BannerWebFirstFramePNG from '@/assets/images/home/banner-web-first-frame.png'
import CountdownSVG from '@/assets/images/home/countdown.svg'
import DownloadSVG from '@/assets/images/home/download.svg'
import ExtensionSVG from '@/assets/images/home/extension.svg'
import FacebookSVG from '@/assets/images/home/facebook.svg'
import GoogleDriveSVG from '@/assets/images/home/google-drive.svg'
import IosSVG from '@/assets/images/home/ios.svg'
import MacSVG from '@/assets/images/home/mac.svg'
import MagnetSVG from '@/assets/images/home/magnet.svg'
import MegaSVG from '@/assets/images/home/mega.svg'
import PcDownloadSVG from '@/assets/images/home/pc-download.svg'
import PcSVG from '@/assets/images/home/pc.svg'
import Avatar1 from '@/assets/images/home/pic_1.png'
import Avatar2 from '@/assets/images/home/pic_2.png'
import Avatar3 from '@/assets/images/home/pic_3.png'
import Avatar4 from '@/assets/images/home/pic_4.png'
import Avatar5 from '@/assets/images/home/pic_5.png'
import PikpakSVG from '@/assets/images/home/pikpak.svg'
import PlatformMACH5SVG from '@/assets/images/home/platform-mac-h5.svg'
import PlatformPCH5SVG from '@/assets/images/home/platform-pc-h5.svg'
import PlatformTelegramH5SVG from '@/assets/images/home/platform-telegram-h5.svg'
import PlatformTVH5SVG from '@/assets/images/home/platform-tv-h5.svg'
import PlatformWebH5SVG from '@/assets/images/home/platform-web-h5.svg'
import QrcodeSVG from '@/assets/images/home/qrcode.svg'
import QuestionSVG from '@/assets/images/home/question.svg'
import rightArrowSVG from '@/assets/images/home/right-arrow.svg'
import SafeDeviceH5PNG from '@/assets/images/home/safe-device-h5.png'
import SafePrivateCloudH5PNG from '@/assets/images/home/safe-private-cloud-h5.png'
import SafeTorrentH5PNG from '@/assets/images/home/safe-torrent-h5.png'
import TelegramSVG from '@/assets/images/home/telegram.svg'
import TiktokSVG from '@/assets/images/home/tiktok.svg'
import TorrentSVG from '@/assets/images/home/torrent.svg'
import TvSVG from '@/assets/images/home/tv.svg'
import TwitterSVG from '@/assets/images/home/twitter.svg'
import WebDAVSVG from '@/assets/images/home/web-dav.svg'
import WebSVG from '@/assets/images/home/web.svg'
import DownIcon from '@/components/icons/DownIcon.vue'
import useDomain from '@/composables/useDomain'
import { useLayout } from '@/store/layout'
import { useUserInfo } from '@/store/userInfo'
import $track from '@/track/webOfficialWebsite'
import {
  AppInstallPlatform,
  InjectionKey,
  RoutePath,
  RoutePathOfDrive,
  RoutePathOfHelp,
  SupportLocale,
} from '@/types/enum'
import {
  ElCarousel,
  ElInput,
  ElPopover,
} from 'element-plus'
import 'element-plus/theme-chalk/el-input.css'
import lottie from 'lottie-web'
import uaParser from 'ua-parser-js'
import { hydrateOnVisible } from 'vue'
import {
  type ExternalProductsPrice,
  getExternalProductsPrice,
  getHomeMediaResource,
  getHomeSpeedAndFileData,
  type HomeSpeedAndFileData,
} from '~/api'
import {
  getGoodsList,
  getGoodsListWithoutAuth,
} from '~/api/payment'
import AppInstallEntries from '~/components/home/AppInstallEntries.vue'
import bannerH5Data from '~/components/home/lottie-data/banner-H5.json'
import bannerWebCloseData from '~/components/home/lottie-data/banner-web-close.json'
import bannerWebOpenData from '~/components/home/lottie-data/banner-web-open.json'
import safeH5Data from '~/components/home/lottie-data/safe-H5.json'
import safeWebData from '~/components/home/lottie-data/safe-web.json'
import ResolveSource from '~/components/home/resolve-source/ResolveSource.vue'
import { appInstallLink } from '~/composables/useInstallChannels'
import usePageTitle from '~/composables/usePageTitle'
import {
  APPLE_SILICON_DOWNLOAD_LINK,
  INTEL_DOWNLOAD_LINK,
} from '~/types/constants'
import { formatNumber } from '~/utils'

const enum SwipeDirection {
  RIGHT = 'RIGHT',
  LEFT = 'LEFT',
}

const PlatformCard = defineAsyncComponent({
  loader: () => import('@/components/home/PlatformCard.vue'),
  hydrate: hydrateOnVisible(),
})

const PriceCard = defineAsyncComponent({
  loader: () => import('@/components/home/PriceCard.vue'),
  hydrate: hydrateOnVisible(),
})

const Countdown = defineAsyncComponent({
  loader: () => import('@/components/home/Countdown.vue'),
  hydrate: hydrateOnVisible(),
})

const SpeedChart = defineAsyncComponent({
  loader: () => import('@/components/home/SpeedChart.vue'),
  hydrate: hydrateOnVisible(),
})

const userInfo = useUserInfo()

const showLoginDialog = inject<ShowLoginDialog>(InjectionKey.SHOW_LOGIN_DIALOG)

const isAndroid = uaParser(useRequestHeaders(['user-agent'])['user-agent']).os.name === 'Android'

const domain = useDomain()
const { t } = useI18n()
const { isMobile } = toRefs(useLayout())

const locale = useI18n().locale as ComputedRef<SupportLocale>
const pageTitle = usePageTitle(t('basic-home-page'))

useSeoMeta({
  title: pageTitle,
  ogTitle: pageTitle,
  ogImage: {
    url: ogImage,
    width: 1340,
    height: 1200,
  },
})

const avatars = [Avatar1, Avatar2, Avatar3, Avatar4, Avatar5]

const carouselSwipeIndex = ref(0)
const carouselWrap = ref<HTMLElement>()

const bannerAnimationContainer = ref<HTMLElement>()
const safeAnimationContainer = ref<HTMLElement>()
const safeAnimationH5Container = ref<HTMLElement>()

const inputValue = ref('')
const inputPopoverRef = ref<InstanceType<typeof ElPopover>>()
const inputPopoverVisible = ref(false)
const inputRef = ref<HTMLElement>()

const priceCarouselRef = ref<typeof ElCarousel>()

const safeH5CarouselRef = ref<typeof ElCarousel>()

const carouselItemStartX = ref(0)
const carouselItemStartY = ref(0)
const carouselItemMoveX = ref(0)
const carouselItemMoveY = ref(0)
const carouselStartTime = ref(0)

const speedData = ref<HomeSpeedAndFileData[]>([])

const videoRef = ref<HTMLVideoElement>()

const subTitleConfig = computed(() => [
  {
    class: 'speed',
    title: t('home-page-subtitle-1'),
  },
  {
    class: 'secret',
    title: t('home-page-subtitle-2'),
  },
  {
    class: 'amazing',
    title: t('home-page-subtitle-3'),
  },
])

const handleWindowsDownload = () => {
  window.open(appInstallLink(AppInstallPlatform.WINDOWS))
}

const platformCardData = computed(() => [
  {
    id: 1,
    cardTitle: 'Android',
    cardSvg: AndroidSVG,
    cardText: t('home-platform-card-data-android-text'),
    topRightAngle: QrcodeSVG,
    hoverSvg: `https://${domain.value}/mobile-app?action=install`,
    hoverTitle: t('home-platform-card-data-android-hover-title'),
    hoverText: t('home-platform-card-data-android-hover-text'),
    linkUrl: RoutePath.MOBILE_APP,
  },
  {
    id: 0,
    cardTitle: 'iOS',
    cardSvg: IosSVG,
    cardText: t('home-platform-card-data-ios-text'),
    topRightAngle: QrcodeSVG,
    hoverSvg: `https://${domain.value}/mobile-app?action=install`,
    hoverTitle: t('home-platform-card-data-ios-hover-title'),
    hoverText: t('home-platform-card-data-ios-hover-text'),
    linkUrl: RoutePath.MOBILE_APP,
  },
  {
    id: 2,
    cardTitle: 'Windows',
    cardSvg: PcSVG,
    cardText: t('home-platform-windows-h5-subtitle'),
    topRightAngle: DownloadSVG,
    trackName: 'download_pc' as const,
    hoverSvg: PcDownloadSVG,
    hoverTitle: t('click-to-download'),
    hoverText: `${t('package-size')}: 95.6M`,
    onClick: handleWindowsDownload,
  },
  {
    id: 6,
    cardTitle: 'macOS',
    cardSvg: MacSVG,
    cardText: t('home-platform-card-data-mac-text'),
    topRightAngle: DownloadSVG,
    trackName: 'download_mac' as const,
    hoverSvg: PcDownloadSVG,
    hoverTitle: t('home-platform-card-data-mac-hover-title'),
    hoverHyperlinks: [
      {
        name: t('home-platform-card-data-mac-hover-hyperlink-intel'),
        url: INTEL_DOWNLOAD_LINK,
      },
      {
        name: t('home-platform-card-data-mac-hover-hyperlink-silicon'),
        url: APPLE_SILICON_DOWNLOAD_LINK,
      },
    ],
    onClick: () => {
      window.open(RoutePath.DESKTOP_APP)
    },
  },
  {
    id: 7,
    cardTitle: 'TV APP',
    cardSvg: TvSVG,
    cardText: t('home-platform-card-data-android-text'),
    topRightAngle: DownloadSVG,
    trackName: 'download_tv' as const,
    hoverSvg: PcDownloadSVG,
    hoverTitle: t('click-to-download'),
    hoverText: `${t('package-size')}: 93.6M`,
    onClick: () => {
      window.open(appInstallLink(AppInstallPlatform.TV_APK))
    },
  },
  {
    id: 3,
    cardTitle: t('home-platform-card-data-web-title'),
    cardSvg: WebSVG,
    cardText: t('home-platform-card-data-web-text'),
    hoverSvg: WebSVG,
    hoverTitle: t('home-platform-card-data-web-hover-title'),
    hoverText: t('home-platform-card-data-web-hover-text'),
    linkUrl: RoutePathOfDrive.ALL,
    trackName: 'web_client' as const,
  },
  {
    id: 4,
    cardTitle: t('home-platform-card-data-extension-title'),
    cardSvg: ExtensionSVG,
    cardText: t('home-platform-card-data-extension-text'),
    topRightLabel: t('home-platform-card-data-extension-label'),
    hoverSvg: QuestionSVG,
    hoverTitle: t('home-platform-card-data-extension-hover-title'),
    hoverText: t('home-platform-card-data-extension-hover-text'),
    linkUrl: RoutePath.BROWSER_EXTENSION,
    trackName: 'go_extensions' as const,
  },
  {
    id: 5,
    cardTitle: 'Telegram Bot',
    cardSvg: TelegramSVG,
    cardText: t('home-platform-card-data-telegram-text'),
    topRightLabel: t('home-platform-card-data-telegram-label'),
    hoverSvg: QuestionSVG,
    hoverTitle: t('home-platform-card-data-telegram-hover-title'),
    hoverText: t('home-platform-card-data-telegram-hover-text'),
    linkUrl: RoutePathOfHelp.TELEGRAM_BOT,
    trackName: 'go_telegram' as const,
  },
  {
    id: 8,
    cardTitle: 'Web DAV',
    cardSvg: WebDAVSVG,
    cardText: t('home-platform-card-web-dav-text'),
    // trackName: 'download_tv' as const,
    hoverSvg: QuestionSVG,
    hoverTitle: t('home-platform-card-data-telegram-hover-title'),
    hoverText: t('home-platform-card-web-dav-text'),
    linkUrl: `${RoutePath.FAQ}#21`,
  },
])

const homeMediaResource = reactive({
  content_facebook: '',
  content_magnet: '',
  content_tiktok: '',
  content_torrent: '',
  content_twitter: '',
  description_facebook: '',
  description_magnet: '',
  description_tiktok: '',
  description_torrent: '',
  description_twitter: '',
})

const productPrice = reactive({
  megaPrice: '0',
  googlePrice: '0',
  pikpakPrice: '0',
  pikpakSalePrice: '0',
  hasDiscount: false, // 是否有折扣
  priceUnit: '',
})

const priceCardData = computed(() => [
  {
    headerSvg: MegaSVG,
    headerName: 'MEGA',
    priceValue: productPrice.megaPrice,
    linkUrl: 'https://mega.nz/pro',
    isPikpak: false,
    trackName: 'go_mega' as const,
  },
  {
    headerSvg: PikpakSVG,
    headerName: 'PikPak',
    priceValue: productPrice.pikpakPrice,
    linkUrl: RoutePathOfDrive.PAYMENT,
    isPikpak: true,
    trackName: 'go_pikpak' as const,
  },
  {
    headerSvg: GoogleDriveSVG,
    headerName: 'Google Drive',
    priceValue: productPrice.googlePrice,
    linkUrl: 'https://one.google.com/about/plans',
    isPikpak: false,
    trackName: 'go_google_drive' as const,
  },
])

const saveDiscount = computed(() => {
  const megaPrice = Number(productPrice.megaPrice.replace(/,/g, ''))
  const pikpakPrice = Number(productPrice.pikpakPrice.replace(/,/g, ''))
  if (!megaPrice || !pikpakPrice) return 0
  const discount = (megaPrice - pikpakPrice) / megaPrice
  return Math.round(discount * 100)
})

const platformNav = computed(() => [
  {
    id: 0,
    type: 'Magnet' as const,
    iconName: MagnetSVG,
    linkUrl: homeMediaResource.content_magnet,
    description: homeMediaResource.description_magnet,
  },
  {
    id: 1,
    type: 'Torrent' as const,
    iconName: TorrentSVG,
    linkUrl: homeMediaResource.content_torrent,
    description: homeMediaResource.description_torrent,
  },
  {
    id: 2,
    type: 'TikTok' as const,
    iconName: TiktokSVG,
    linkUrl: homeMediaResource.content_tiktok,
    description: homeMediaResource.description_tiktok,
  },
  {
    id: 3,
    type: 'X (Twitter)' as const,
    iconName: TwitterSVG,
    linkUrl: homeMediaResource.content_twitter,
    description: homeMediaResource.description_twitter,
  },
  {
    id: 4,
    type: 'Facebook' as const,
    iconName: FacebookSVG,
    linkUrl: homeMediaResource.content_facebook,
    description: homeMediaResource.description_facebook,
  },
].filter(el => el.linkUrl))

const safeH5ContentData = computed(() => [
  {
    imgUrl: SafeTorrentH5PNG,
    title: t('home-safe-url-title'),
    describe: t('home-safe-url-content'),
  },
  {
    imgUrl: SafePrivateCloudH5PNG,
    title: t('home-safe-cloud-title'),
    describe: t('home-safe-cloud-content'),
  },
  {
    imgUrl: SafeDeviceH5PNG,
    title: t('home-safe-device-title'),
    describe: t('home-safe-device-content'),
  },
])

const resolveResourceState = reactive<{
  visible: boolean
  errorMessage?: string
}>({
  visible: false,
  errorMessage: undefined,
})
const resolveResourceInstance = ref<InstanceType<typeof ResolveSource>>()

const resolveSourceUrl = ref<string>()
const handleParse = async () => {
  inputPopoverVisible.value = false
  if (resolveResourceState.errorMessage) {
    resolveResourceState.errorMessage = ''
  }
  if (!inputValue.value) {
    inputPopoverVisible.value = true
    resolveResourceState.visible = false
    $track.web_official_input_result({ result: 'null' })
    return
  }

  const validURLOrURN = /^(?:\s*(?:magnet:\?|https?:\/\/|ftp:\/\/|thunder:\/\/|ed2k:\/\/)[^\r\n\t\v\f]{3,}[\r\n]{0,2})+$/i
  if (!validURLOrURN.test(inputValue.value)) {
    resolveResourceState.errorMessage = t('resolve-source-base-error')
    $track.web_official_input_result({ result: 'null' })
    return
  }

  resolveSourceUrl.value = inputValue.value
  Object.assign(resolveResourceState, {
    visible: true,
  })
}

const handleLinkClick = (linkUrl: string) => {
  inputValue.value = linkUrl
}

const handleInputClick = () => {
  $track.web_official_input_click()
  Object.assign(resolveResourceState, { errorMessage: undefined })
  if (inputPopoverVisible.value === true) {
    return
  }

  inputPopoverVisible.value = !inputPopoverVisible.value
}

const handleInputBlur = () => {
  if (inputPopoverVisible.value === false) {
    return
  }

  inputPopoverVisible.value = !inputPopoverVisible.value
}

const handleInput = (str: string) => {
  if (str.length === 0) {
    inputPopoverVisible.value = true
    return
  }

  inputPopoverVisible.value = false
}

const handleStartPikpak = () => {
  if (!userInfo.userId) {
    showLoginDialog?.({
      afterLogin: () => {
        window.location.href = RoutePathOfDrive.ALL
      },
    })
    return
  }

  window.location.href = RoutePathOfDrive.ALL
}

const bannerWebLottieAnimation = () => {
  if (isMobile.value) {
    bannerAnimationContainer.value && lottie.loadAnimation({
      container: bannerAnimationContainer.value,
      renderer: 'svg',
      animationData: bannerH5Data,
      autoplay: true,
      loop: true,
    })
    return
  }
  if (!bannerAnimationContainer.value) return
  const bannerAnimationClose = lottie.loadAnimation({
    container: bannerAnimationContainer.value,
    renderer: 'svg',
    animationData: bannerWebCloseData,
    autoplay: false,
    loop: false,
  })

  bannerAnimationClose.play()

  bannerAnimationClose.addEventListener('complete', () => {
    bannerAnimationClose.destroy()
  })

  bannerAnimationClose.addEventListener('destroy', () => {
    if (!bannerAnimationContainer.value) return
    const bannerAnimationOpen = lottie.loadAnimation({
      container: bannerAnimationContainer.value,
      renderer: 'svg',
      animationData: bannerWebOpenData,
      autoplay: false,
      loop: false,
    })
    bannerAnimationOpen.play()
  })
}

const handleResolveSourceClose = () => {
  inputValue.value = ''
  resolveResourceState.errorMessage = ''
}

const safeWebLottieAnimation = () => {
  if (isMobile.value) {
    safeAnimationH5Container.value && lottie.loadAnimation({
      container: safeAnimationH5Container.value,
      renderer: 'svg',
      animationData: safeH5Data,
    })
    return
  }
  safeAnimationContainer.value && lottie.loadAnimation({
    container: safeAnimationContainer.value,
    renderer: 'svg',
    animationData: safeWebData,
  })
}

// 首次在服务端执行
const { data: speedAndFileData } = await getHomeSpeedAndFileData(true)
if (speedAndFileData.value) {
  speedData.value = speedAndFileData.value
}
const handleGetSpeedData = async () => {
  const { data } = await getHomeSpeedAndFileData(false)
  if (data.value) {
    speedData.value = data.value
  }
}

const handleCarouselPrev = (type: string) => {
  // TODO 待优化
  if (type === 'safe') {
    safeH5CarouselRef.value?.prev()
    carouselStartTime.value = new Date().getTime() + 500
  }

  priceCarouselRef.value?.prev()
  carouselStartTime.value = new Date().getTime() + 500
}

const handleCarouselNext = (type: string) => {
  // TODO 待优化
  if (type === 'safe') {
    safeH5CarouselRef.value?.next()
    carouselStartTime.value = new Date().getTime() + 500
  }

  priceCarouselRef.value?.next()
  carouselStartTime.value = new Date().getTime() + 500
}

const handleCarouselTouchstart = (e: TouchEvent) => {
  carouselItemStartX.value = e.touches[0].clientX
  carouselItemStartY.value = e.touches[0].clientY
}

const handleCarouselTouchmove = (e: TouchEvent, type: string) => {
  carouselItemMoveX.value = e.touches[0].clientX
  carouselItemMoveY.value = e.touches[0].clientY
  const nowTime = new Date().getTime()
  if (carouselStartTime.value === undefined || nowTime > carouselStartTime.value) {
    if (carouselItemStartX.value - carouselItemMoveX.value <= 0) {
      handleCarouselPrev(type)
      return false
    }
    handleCarouselNext(type)
    return false
  }
}

const getGoodsPrice = async (language: SupportLocale) => {
  const result = {
    megaPrice: '0',
    googlePrice: '0',
    pikpakPrice: '0',
    pikpakSalePrice: '0',
    hasDiscount: false,
    priceUnit: '',
  }
  const { data: config } = await getExternalProductsPrice(language)
  const productPriceData = config.value as ExternalProductsPrice

  if (!productPriceData) return

  const externalProductsData = productPriceData.values ? productPriceData.values.external_products : {}
  result.googlePrice = externalProductsData.google_product?.price || '0'
  result.megaPrice = externalProductsData.mega_product?.price || '0'
  result.pikpakPrice = externalProductsData.pikpak_product?.price || '0'
  result.pikpakSalePrice = externalProductsData.pikpak_product?.sale_price || '0'
  try {
    const isLogin = !!userInfo.userId
    const {
      data,
      error,
    } = await (isLogin ? getGoodsList({ scene: 'official' }, language) : getGoodsListWithoutAuth(
      'official',
      language,
    ))
    if (error.value || !data.value) {
      console.log('getGoodsPrice error:', error.value)
      return result
    }
    // 返回订阅年和一次性年,第一个discount非空的年商品来确认是否可以享受优惠
    // 第一个discount非空的年商品为优惠年商品，第一个discount为空的年商品为正常价年商品
    const products = data.value.products
    if (products) {
      const discountYearProduct = products.find((item: GoodProduct) => item.interval === 'year' && item.discount)
      const normalYearProduct = products.find((item: GoodProduct) => item.interval === 'year' && !item.discount)
      if (normalYearProduct) {
        result.pikpakPrice = formatNumber(`${(Number(normalYearProduct.unit_amount.replace(
          /,/g,
          '',
        )) / 12 / 10).toFixed(2)}`)
        result.priceUnit = normalYearProduct.unit
      }
      if (discountYearProduct) {
        result.pikpakSalePrice = formatNumber(`${(Number(discountYearProduct.unit_amount.replace(
          /,/g,
          '',
        )) / 12 / 10).toFixed(2)}`)
        result.hasDiscount = true
      }
    }
  } catch (err) {
    console.log('getGoodsPrice error:', err)
  }
  return result
}

const updateExternalProductsPrice = async (language: SupportLocale) => {
  const prices = await getGoodsPrice(language)
  Object.assign(productPrice, prices)
}

const updateHomeMediaResource = async (language: SupportLocale) => {
  const { data: resourceData } = await getHomeMediaResource(language)
  if (!resourceData.value) return

  // TODO 上游类型错误导致类型推导失败
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const homeMediaResourceData = resourceData.value.values.resources

  homeMediaResource.content_facebook = homeMediaResourceData.content_facebook
  homeMediaResource.content_magnet = homeMediaResourceData.content_magnet
  homeMediaResource.content_tiktok = homeMediaResourceData.content_tiktok
  homeMediaResource.content_torrent = homeMediaResourceData.content_torrent
  homeMediaResource.content_twitter = homeMediaResourceData.content_twitter
  homeMediaResource.description_facebook = homeMediaResourceData.description_facebook
  homeMediaResource.description_magnet = homeMediaResourceData.description_magnet
  homeMediaResource.description_tiktok = homeMediaResourceData.description_tiktok
  homeMediaResource.description_torrent = homeMediaResourceData.description_torrent
  homeMediaResource.description_twitter = homeMediaResourceData.description_twitter
}

watchEffect(() => {
  updateExternalProductsPrice(locale.value)
  updateHomeMediaResource(locale.value)
})

watch(
  [isMobile, safeAnimationContainer],
  async (
    [newIsMobile, newElt],
  ) => {
    await nextTick()
    !newIsMobile && newElt && setTimeout(safeWebLottieAnimation, 0)
  },
)

const trackAreaVisible = (viewName: Parameters<typeof $track.web_official_main_page_show>[0]['view']) => {
  $track.web_official_main_page_show({ view: viewName })
}

// eslint-disable-next-line max-len
const trackMainPageClick = (trackName: Parameters<typeof $track.web_official_main_page_click>[0]['button']) => {
  $track.web_official_main_page_click({ button: trackName })
}

const handleVideoPlay = () => {
  if (!videoRef.value) return
  (videoRef.value.firstChild as HTMLSourceElement).src = 'https://download.mypikpak.com/video/quality-compare.mp4'
  videoRef.value?.load()
  videoRef.value?.play()
}

const trackInputDemoClick = (trackName: Parameters<typeof $track.web_official_demo_click>[0]['type']) => {
  $track.web_official_demo_click({ type: trackName })
}

/* 自定义指令 */
const vSaw = {
  mounted: (el: HTMLElement, binding: { value: () => void }) => {
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].intersectionRatio >= 0.9) {
          binding.value()
          io.unobserve(el)
        }
      },
      {
        threshold: 0.9,
      },
    )
    io.observe(el)
  },
}

onMounted(() => {
  bannerWebLottieAnimation()
  nextTick(() => {
    if (!isMobile.value) return
    priceCarouselRef.value?.setActiveItem(1)
  })
})

const feedbackNumbers = [
  '52***ku',
  '李**了',
  'IKi***u',
  'm***te',
  'fh**ro',
  '旅***行',
  '周**明',
  'Xi***ing',
  '颜**光',
  'Hu**ing',
  'Mao**Li',
  'gu**yang',
  '我**22',
  'c**cc',
  'Kh**sin',
  'Th**ore',
  'Goo**户',
  'ave**Li',
  'lg**99',
  '杨**锋',
]
const transitionX = computed(() => {
  const wrapWidth = carouselWrap.value?.getClientRects()[0]?.width || 0
  const baseWidth = wrapWidth + (carouselSwipeIndex.value ? 24 : 0)
  return `-${carouselSwipeIndex.value * baseWidth}px`
})
const steps = computed(() => Math.ceil(feedbackNumbers.length / 4))

const swipeLeftEnabled = computed(() => carouselSwipeIndex.value > 0)
const swipeRightEnabled = computed(() => carouselSwipeIndex.value + 1 < steps.value)

const attrAssert = (assert: boolean) => (assert
  ? {
    class: 'enabled',
    color: '#fff',
  }
  : {
    class: '',
    color: '#ababab',
  })

const swipeBtnAttr = (direction: SwipeDirection) => (direction === SwipeDirection.LEFT
  ? attrAssert(swipeLeftEnabled.value)
  : attrAssert(swipeRightEnabled.value))

const swipe = (direction: SwipeDirection) => {
  if (direction === SwipeDirection.LEFT && !swipeLeftEnabled.value) return
  if (direction === SwipeDirection.RIGHT && !swipeRightEnabled.value) return

  const step = direction === SwipeDirection.RIGHT ? 1 : steps.value - 1
  carouselSwipeIndex.value = (carouselSwipeIndex.value + step) % steps.value
}

definePageMeta({
  layout: 'basic',
  middleware: 'disable-mobile-platform-card-anchor',
})
</script>

<style
  lang="less"
  scoped
>
@import '@/assets/styles/util.less';

.home-page {
  .banner {
    position: relative;
    background: url('@/assets/images/home/banner-bg.svg') var(--no-repeat-center) / cover;
    height: 884px; // 锁定高度，因为有资源解析弹窗
    padding-top: 64px;
    display: flex;
    flex-direction: column;
    align-items: center;

    &-web-first-frame {
      width: 100%;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    &-main {
      width: 943px;
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-top: 84px;

      .public-page {
        &-title {
          font-size: 42px;
          line-height: 63px;
          text-align: center;
        }

        &-subtitle {
          #flex-center();
          flex-wrap: wrap;
          justify-content: center;
          margin-top: 12px;
          line-height: 24px;
          font-size: 16px;
          color: var(--color-secondary-text);

          > span {
            position: relative;
            #flex-center();

            + span {
              margin-left: 36px;

              &:before {
                content: '';
                position: absolute;
                width: 2px;
                height: 16px;
                display: block;
                background: rgba(152, 162, 173, 0.6);
                left: -17px;
              }
            }
          }

          .icon {
            display: inline-block;
            width: 16px;
            height: 16px;
            margin-right: 8px;
          }

          @icons: speed, secret, amazing;
          each(@icons, {
            .@{value} .icon {
              @img: '@/assets/images/home/subtitle-@{value}.svg';
              background: url(@img) var(--no-repeat-center);
            }
          });
        }

        &-input {
          padding: 7px 0;
          margin-top: 48px;
          display: flex;
          align-items: center;
          background: #fcfdff;
          box-shadow: 0 4px 16px rgba(134, 163, 184, 0.2);
          border-radius: 12px;
          color: #000;
        }
      }

      .el-input {
        width: 800px;
        height: 58px;
        --el-input-focus-border-color: none;
        --el-input-hover-border-color: none;
        background: transparent;

        &__wrapper {

        }

        &-group__append {
          width: 132px;
          border-radius: 0 12px 12px 0;
          background: #306eff;

          font-weight: 600;
          font-size: 18px;
          line-height: 72px;
          color: #fff;
        }

        :deep(.el-input__inner) {
          font-size: 16px;
        }
      }

      .el-button {
        width: 160px;
        height: 58px;
        border-radius: 12px;
        background: #306eff;
        font-weight: 600;
        font-size: 18px;
        color: #fff;
        position: absolute;
        white-space: pre-wrap;
        overflow: hidden;
        line-height: 1;
        right: 7px;
      }
    }

    &-animation-container {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: -1;
    }
  }

  .highlight-explain {
    &-title {
      height: 59px;
      font-weight: 600;
      font-size: 42px;
      line-height: 1.5em;
      color: #000;
      text-align: center;
    }

    .content {
      @media screen and (max-width: 1200px) {
        margin-top: 27px;
      }
      margin-top: 108px;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .title {
      display: flex;
      justify-content: center;

      h2 {
        span {
          font-weight: 600;
          font-size: 32px;
          line-height: 45px;
          color: #fff;
        }
      }

      .end {
        opacity: 0.6;
        margin-left: 16px;
      }
    }

    .subtitle {
      width: 634px;
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
      color: #fff;
      opacity: 0.6;
      margin-top: 16px;
      text-align: center;
    }

    .start-pikpak {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 24px;
      cursor: pointer;

      span {
        font-weight: 600;
        font-size: 16px;
        line-height: 22px;
        color: #306eff;
        margin-right: 14px;
        cursor: pointer;
      }
    }

    .summary {
      font-weight: 400;
      font-size: 13px;
      line-height: 18px;

      color: #fff;
      opacity: 0.6;
    }

    .speed {
      &-explain {
        background: url('@/assets/images/home/speed-bg.png') center bottom no-repeat;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding-bottom: 120px;
        background-size: 100% 100%;
      }

      &-content {
        margin-top: 188px;
      }

      &-chart {
      }
    }

    .safe {
      &-explain {
        background: #1a1b20;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding-bottom: 54px;
      }

      &-animation-container {
        @media screen and (max-width: 1200px) {
          margin-top: 24px;
        }
        position: relative;
        margin-top: 84px;
        height: 316px;

        .content-box {
          position: absolute;
          width: 400px;
          text-align: center;
          color: #fff;

          &.url {
            top: 300px;
            left: 0;
            transform: translate(-16px, 0);
          }

          &.private-cloud {
            left: 50%;
            top: 0;
            transform: translate(-212px, 172px);
          }

          &.device {
            top: 300px;
            right: 0;
            transform: translate(-2px, 0);
          }

          .name {
            font-weight: 600;
            font-size: 24px;
            line-height: 34px;
          }

          .detail {
            font-weight: 400;
            font-size: 16px;
            line-height: 24px;
            opacity: 0.6;
          }
        }
      }

      &-summary {
        margin-top: 172px;
      }
    }

    .video {
      &-explain {
        background: #000;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding-bottom: 54px;
      }

      &-diff-container {
        width: min(940px, 80%);
        margin-top: 56px;

        video {
          width: 100%;
        }
      }

      &-diff-content {
        margin-top: 28px;
        width: 940px;
        height: 56px;
        display: flex;
        line-height: 23px;
        color: #fff;

        .content-box {
          width: 470px;
          display: flex;
          align-items: center;
          flex-direction: column;
          text-align: center;

          .image-quality {
            font-weight: 600;
            font-size: 24px;
          }

          .website-explain {
            margin-top: 8px;
            font-size: 16px;
            font-weight: 400;
            opacity: 0.6;
          }
        }
      }

      &-summary {
        margin-top: 70px;
      }
    }
  }
}

.platform {
  &-display {
    width: 100%;
    display: flex;
    align-items: center;
    flex-direction: column;
    padding-bottom: 108px;
  }

  &-title {
    margin-top: 108px;
    margin-bottom: 52px;
    font-size: 42px;
    font-weight: 600;
    line-height: 1.5em;
    text-align: center;
  }

  &-card-container {
    ul {
      max-width: 1400px;
      display: flex;
      flex-wrap: wrap;
      gap: 24px;
      justify-content: center;
    }
  }
}

.price {
  &-comparison {
    width: 100%;
    background: url('@/assets/images/home/price-comparison-bg.jpg') var(--no-repeat-center)/cover;
    display: flex;
    align-items: center;
    flex-direction: column;
    padding-bottom: 47px;
  }

  &-title {
    margin-top: min(108px, 7.5vw);
    margin-bottom: min(72px, 5vw);
    font-size: 42px;
    font-weight: 600;
    line-height: 1.5em;
  }

  &-card-container {
    width: min(1128px, 75.2%);
    display: flex;
    align-items: center;
    gap: 0 24px;
    justify-content: space-between;
  }

  &-sale-promotion {
    text-align: center;

    .limit-tips {
      font-size: 13px;
      color: var(--color-secondary-text);
      margin: 24px auto 0;
      line-height: 18px;
      max-width: 800px;
    }

    .price-countdown {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 48px;

      .content {
        display: flex;
        align-items: center;
      }

      .text {
        margin: 0 12px;
        font-weight: 500;
        font-size: 16px;
        line-height: 15px;
        color: #ff663b;
      }
    }

    .today-price {
      margin-top: 16px;
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
      color: #000;

      .value {
        font-size: 24px;
        color: #306eff;
        font-weight: bold;
      }
    }

    .get-discount {
      margin-top: 24px;
      width: 200px;
      height: 48px;
      background: linear-gradient(0deg, #306eff, #306eff), linear-gradient(270deg, #7b38ff 0%, #3d6eff 99.93%);
      border-radius: 8px;
      border: none;

      font-weight: 600;
      font-size: 16px;
      line-height: 22px;
      color: #fff;
    }

    .tips {
      font-weight: 400;
      font-size: 13px;
      line-height: 18px;
      color: #666;
      max-width: 800px;
      margin: 12px auto;
    }
  }
}

.feedbacks {
  padding: 70px 0 54px;
  background: url('@/assets/images/home/feedback-bg.svg') var(--no-repeat-center) / cover;

  h2 {
    text-align: center;
    font-weight: 600;
    font-size: 32px;
  }

  .home-feedbacks-subtitle {
    color: #666;
    font-size: 16px;
    font-weight: 400;
    text-align: center;
    margin-top: 12px;
  }

  .carousel-wrap {
    overflow: hidden;
  }

  .carousel {
    transition: transform ease-in-out 0.8s;
    margin-top: 24px;
    display: flex;

    li {
      --width: calc((100% - 72px) / 4);
      flex: 0 0 var(--width);
      min-height: 320px;
      padding: 16px;
      background: url('@/assets/images/home/quote.svg') no-repeat calc(100% - 16px) calc(100% - 16px), #fff;
      box-shadow: 0 6px 12px rgba(23, 71, 165, 0.02);
      border-radius: 16px;

      + li {
        margin-left: 24px;
      }
    }
  }

  .info {
    display: flex;
    align-items: center;
  }

  .avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #eef0f2;
  }

  .name {
    font-weight: 600;
    line-height: 20px;
    margin-left: 8px;
  }

  .feedback {
    margin-top: 36px;
    font-size: 16px;
    line-height: 24px;
  }

  .carousel-controller {
    margin-top: 54px;
    display: flex;
    justify-content: center;

    a {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 54px;
      height: 54px;
      border-radius: 50%;
      box-shadow: 0 2px 10px rgba(37, 113, 181, 0.1);
      cursor: not-allowed;
      background: #fff;

      &.enabled {
        cursor: pointer;
        background: var(--color-primary);
      }

      + a {
        margin-left: 32px;
      }
    }
  }
}

@media screen and (max-width: @mobile-break-width) {
  .home-page {
    width: calc(100vw);

    .banner {
      height: auto;
      width: 100%;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding-top: 0;

      &-main {
        width: 100vw;
        padding: 0 20px;
        margin-top: 48px;

        .public-page {
          &-title {
            font-weight: 600;
            font-size: 24px;
            line-height: 36px;
            color: #000;
          }

          &-subtitle {
            margin-top: 2px;
            font-size: 14px;

            > span {
              margin-top: 6px;
            }

            .icon {
              margin-right: 4px;
            }
          }
        }
      }

      &-animation-container {
        position: static;
        bottom: 0;
        top: 0;
        left: 0;
        transform: none;

        &.android {
          margin-top: 54px;
        }
      }
    }

    .highlight-explain {
      &-title {
        font-size: 24px;
        margin-top: 54px;
        margin-bottom: 32px;
      }

      .title {
        font-weight: 600;
        color: #fff;
        margin-top: 54px;

        h2 {
          text-align: center;

          span {
            font-size: 20px;
            line-height: 28px;
          }

          .front {
            word-break: break-all;
          }
        }
      }

      .subtitle {
        width: 320px;
        font-weight: 400;
        font-size: 14px;
        line-height: 24px;
      }

      .start-pikpak {
        label {
          font-size: 14px;
          line-height: 20px;
        }
      }

      .content {
        width: 320px;
      }

      .summary {
        width: 320px;
        text-align: center;
      }

      .speed {
        &-explain {
          padding-bottom: 42px;
          background-image: url('@/assets/images/home/speed-h5-bg.svg'), linear-gradient(#000, #000);
          background-size: 100% auto, 100% 100px;
          background-position: center top, center bottom;
          background-repeat: no-repeat;
        }

        &-content {
          margin-top: 0;

          h2 {
            text-align: center;
          }
        }

        &-chart {
          width: 100%;
        }
      }

      .safe {
        &-content {
          margin-top: 0;
        }

        &-h5-content {
          width: 320px;
          margin-top: 48px;
          text-align: center;

          .content-box {
            color: #fff;

            .title {
              font-weight: 600;
              font-size: 16px;
              line-height: 22px;
              margin-top: 16px;
            }

            .describe {
              font-weight: 400;
              font-size: 14px;
              line-height: 20px;
              opacity: 0.6;
              margin-top: 8px;
            }
          }
        }

        &-animation-h5-container {
          width: 196px;
          height: 32px;
          display: inline-block;
          margin-top: 36px;
        }

        &-summary {
          margin-top: 54px;
        }
      }

      .video {
        &-content {
          margin-top: 0;
          margin-bottom: 54px;
        }

        &-diff-container {
          width: 320px;
          height: 154px;
          margin-bottom: 24px;
          margin-top: 0;
        }

        &-diff-content {
          margin-top: 0;
          width: 320px;

          .content-box {
            width: 160px;

            .image-quality {
              font-weight: 600;
              font-size: 16px;
              line-height: 22px;
            }

            .website-explain {
              margin-top: 8px;
              font-size: 14px;
              font-weight: 400;
              line-height: 19.6px;
              opacity: 0.6;
            }
          }
        }

        &-summary {
          margin-top: 54px;
        }
      }
    }

    .platform {
      &-display {
        padding-bottom: 0;
      }

      &-title {
        margin-top: 54px;
        font-size: 24px;
        margin-bottom: 32px;
      }

      &-card-container {
        padding: 0;
        width: 100%;

        ul {
          display: block;

          li {
            margin-top: 0;
            text-align: center;

            &:nth-child(odd) {
              background: #f2f5fa;
            }
          }
        }
      }

      &-h5 {
        &-box {
          display: inline-block;
          padding: 0 20px 54px 20px;

          a {
            text-decoration: none;
          }

          &.no-label {
            .platform-h5-img {
              margin-bottom: 24px;
            }
          }
        }

        &-img {
          margin-top: 54px;
          margin-bottom: 26px;
        }

        &-label {
          span {
            font-weight: 400;
            font-size: 12px;
            line-height: 17px;
            color: #ff663b;
            background: rgba(255, 102, 59, 0.1);
            padding: 2px 6px;
            border-radius: 12px;
          }
        }

        &-title {
          margin-top: 8px;
          font-weight: 600;
          font-size: 16px;
          line-height: 22px;
        }

        &-subtitle {
          margin-top: 8px;
          font-weight: 400;
          font-size: 14px;
          line-height: 20px;
          color: #666;
        }

        &-button {
          min-width: 168px;
          height: 48px;
          border-radius: 6px;
          margin-top: 12px;
          font-weight: 600;
          font-size: 16px;
          line-height: 22px;
          color: #306eff;
          background: rgba(255, 255, 255, 0);
          border-color: #306eff;
        }

        &-link {
          display: inline-block;
          margin-top: 24px;
          font-weight: 400;
          font-size: 14px;
          line-height: 22px;
          color: #000;
          pointer-events: none;
          text-decoration: none;
        }
      }
    }
  }

  .price {
    &-comparison {
      background: url('@/assets/images/home/price-comparison-h5-bg.jpg') var(--no-repeat-center)/cover;
      overflow: hidden;
    }

    &-title {
      margin-top: 54px;
      font-weight: 600;
      font-size: 24px;
      line-height: 34px;
      text-align: center;
      margin-bottom: 64px;
      padding: 0 20px;
    }

    &-card-h5-container {
      width: 130vw;
    }

    &-sale-promotion {
      display: flex;
      flex-direction: column;
      align-items: center;

      .price-countdown {
        flex-direction: column;
        margin-top: 43px;

        .content {
          margin-bottom: 16px;

          img {
            width: 16px;
            height: 16px;
          }

          .text {
            font-size: 14px;
            line-height: 15px;
          }
        }
      }

      .today-price {
        width: 276px;
        font-size: 14px;
        line-height: 24px;
      }

      .get-discount {
        margin-top: 24px;
      }

      .limit-tips,
      .tips {
        max-width: 100%;
        padding: 0 32px;
      }
    }
  }

  .feedbacks {
    padding: 36px 0 54px !important;
    background: url('@/assets/images/home/mobile-feedback-bg.svg') no-repeat center/cover;

    h2 {
      font-size: 20px;
      line-height: 1.4em;
    }

    .home-feedbacks-subtitle {
      line-height: 1.4em;
    }

    .carousel-wrap {
      overflow: auto;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    .carousel li {
      --width: calc((100% - 54px));
    }

    .carousel-controller {
      display: none;
    }
  }
}

@media screen and (min-width: @mobile-break-width) and (max-width: @pc-min-width) {
  .home-page {
    .banner {
      min-height: 600px;
      height: auto;

      &-main {
        width: 100vw;
        padding: 0 20px;
        margin-top: 24px;
      }

      .public {
        &-page-input {
          margin-top: 32px;
        }
      }

      &-animation-container {
        transform: translate(-50%, -40%);
      }
    }

    .highlight-explain {
      .safe {
        &-explain {
          padding: 0 20px 54px 20px;
        }

        &-animation-container {
          .content-box {
            width: 282px;

            &.private-cloud {
              transform: translate(-149px, 172px);
            }

            &.url {
              top: 275px;
              left: 0;
              transform: translate(20px, 0);
            }

            &.device {
              top: 275px;
              right: 0;
              transform: translate(-30px, 0);
            }
          }
        }

        &-summary {
          margin-top: 100px;
        }
      }
    }
  }

  .platform {
    &-card-container {
      padding: 0 20px;

      ul {
        justify-content: space-around;
      }

      li {
        width: calc((100% - 24px) / 2);
      }
    }
  }

  .feedbacks {
    .carousel {
      li {
        --width: calc((100% - 48px) / 3);
      }
    }
  }
}

.resolve-source-error {
  position: absolute;
  bottom: -20px;
  color: var(--color-error);
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  width: 100%;
}
</style>
<style lang="less">
@import '@/assets/styles/util.less';

.banner {
  .public-page-input {
    position: relative;

    .el-input {
      display: inline-flex;

      &__inner {
        padding: 0 172px 0 12px;
        border: none;
      }

      .el-input__wrapper {
        box-shadow: none;
        background: #fcfdff;
        border-radius: 12px;
        padding: 1px 11px;
      }
    }
  }
}

.el-popover.pp-input-popover {
  width: 800px !important;
  border-radius: 12px;

  ul {
    li {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .media-type {
        display: inline-flex;
        justify-content: center;
        align-items: center;
        height: 32px;
        margin: 12px 8px 12px 12px;
        background: #306eff1a;
        border-radius: 16px;
        white-space: nowrap;

        img {
          padding-left: 8px;
        }

        label {
          font-weight: 400;
          font-size: 12px;
          line-height: 18px;
          color: #306eff;
          padding-left: 4px;
          padding-right: 8px;
        }
      }

      .media-link {
        display: flex;
        width: 674px;
        font-weight: 400;
        font-size: 16px;
        line-height: 24px;
        height: 24px;
        color: #000;

        &-url {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex-grow: 1;
        }

        &-text {
          flex-shrink: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }

  p {
    margin-top: 6px;
    padding-bottom: 8px;
    text-align: center;
    font-weight: 400;
    font-size: 13px;
    line-height: 18px;
    color: #98a2ad;
  }
}

.home-feature-3-subtitle-3 {
  a {
    position: relative;
    display: inline-block;
    margin-top: 14px;
    text-decoration: none;

    &::after {
      position: absolute;
      content: '>';
      right: -14px;
      user-select: none;
    }
  }
}

@media screen and (max-width: @mobile-break-width) {
  .home-page {
    .public-page-content {
      padding: 0 24px;
    }

    .home-feature-3-subtitle-3 {
      text-align: center !important;
    }

    .install-box {
      margin-top: 30px;
      display: flex !important;
      flex-direction: column;
      align-items: center;
      justify-content: space-evenly;

      a + a {
        margin-top: 12px;
      }

      .icon-install {
        display: block;
        width: 189px;
        height: 54px;
        background-position: center;
        background-size: cover;
      }
    }

    .safe-carousel {

      img {
        width: 160px;
        height: 160px;
      }

      .el-carousel {
        &__indicators {
          margin-top: 22px;
        }

        &__button {
          width: 6px;
          height: 6px;
          border-radius: 3px;
        }
      }
    }

    .price-carousel {
      overflow: hidden;
      padding-top: 8px;
      padding-bottom: 32px;

      .el-carousel {
        &__container {
          overflow: hidden;
        }

        &__indicators {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: 12px;

          li {
            width: 24px;
            height: 24px;
          }

          li:nth-child(1) {
            background: url('@/assets/images/home/home-mega-gray.svg');
            background-size: cover;

            &.is-active {
              background: url('@/assets/images/home/mega.svg');
              background-size: cover;
            }
          }

          li:nth-child(2) {
            background: url('@/assets/images/home/home-pikpak-gray.svg');
            background-size: cover;
            margin: 0 57.5px;

            &.is-active {
              background: url('@/assets/images/home/home-pikpak-blue.svg');
              background-size: cover;
            }

            button {
              width: 0;
              height: 0;
              position: relative;

              &::before {
                content: '';
                position: absolute;
                width: 32.5px;
                height: 3px;
                background: url('@/assets/images/home/dot-1.svg');
                background-size: cover;
                top: -2px;
                right: 17px;
              }

              &::after {
                content: '';
                position: absolute;
                width: 32.5px;
                height: 3px;
                background: url('@/assets/images/home/dot-2.svg');
                background-size: cover;
                top: -2px;
                left: 34px;
              }
            }
          }

          li:nth-child(3) {
            background: url('@/assets/images/home/home-google-drive-gray.svg');
            background-size: cover;

            &.is-active {
              background: url('@/assets/images/home/google-drive.svg');
              background-size: cover;
            }
          }
        }

        &__button {
          width: 0;
          height: 0;
          opacity: 1;
        }

        &__item {
          overflow: visible;
          padding-top: 8px;
        }
      }
    }
  }
}
</style>
